import { type FC, useState } from "react";
import { Checkbox, Group, ScrollArea, Stack } from "@mantine/core";
import CTA from "@/components/atoms/CTA";
import Text from "@/components/atoms/Text";
import { useTranslation } from "react-i18next";
import { ReceptionFractionWindow } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { useGetAllSalesItemsByLanguageId } from "@/services/Product";
import SegmentedControls from "@/components/atoms/SegmentedControls";
import styles from "./FractionWindowPanel.module.css";

type Mode = "ALL" | "NONE" | "MULTI";

interface Props {
  periodLabel: string;
  /** The current window for this period, or undefined if none saved yet */
  window: ReceptionFractionWindow | undefined;
  onClose: () => void;
  /** Called once, with the full window value, when Apply is clicked */
  onApply: (window: Omit<ReceptionFractionWindow, "id">) => void;
  /** Deletes the saved window for this period, reverting it to unconfigured. Undefined when there's nothing to delete. */
  onReset?: () => void;
}

/**
 * Selections are staged locally and only sent to the backend as a single upsert when Apply is
 * clicked — matches the granularity of the underlying endpoint (one PUT/POST per window) instead
 * of firing a request per checkbox click.
 *
 * ALL/NONE/MULTI-SELECT is not independent state — it's derived from the checkbox selection
 * (none checked -> NONE, every item checked -> ALL, anything in between -> MULTI-SELECT). The
 * segmented control is both a status readout and a bulk action: clicking ALL checks everything,
 * clicking NONE unchecks everything, and manually checking/unchecking items moves the derived
 * status between the three automatically.
 */
const FractionWindowPanel: FC<Props> = ({ periodLabel, window: win, onClose, onApply, onReset }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const { data: salesItems = [] } = useGetAllSalesItemsByLanguageId(user?.language.id ?? 1);
  const allIds = salesItems.map((item) => item.salesItemId);

  // The user's explicit root selections — never the backend-expanded `fractionIds`. A window
  // that was previously accept-all has no explicit picks to preserve, so it starts fully checked.
  const [selectedTopLevelIds, setSelectedTopLevelIds] = useState<number[]>(
    win?.acceptAll ? allIds : (win?.topLevelFractionIds ?? [])
  );

  const mode: Mode =
    selectedTopLevelIds.length === 0 ? "NONE" : selectedTopLevelIds.length === allIds.length ? "ALL" : "MULTI";

  const base = {
    granularity: win?.granularity ?? "MONTH",
    periodStart: win?.periodStart ?? "",
  } as const;

  const toggleFraction = (id: number) =>
    setSelectedTopLevelIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const handleModeChange = (value: string) => {
    if (value === "ALL") setSelectedTopLevelIds(allIds);
    else if (value === "NONE") setSelectedTopLevelIds([]);
    // MULTI-SELECT isn't a distinct action — it only ever reflects an already-partial selection.
  };

  const handleApply = () =>
    onApply({
      ...base,
      closed: mode === "NONE",
      acceptAll: mode === "ALL",
      fractionIds: [],
      topLevelFractionIds: mode === "MULTI" ? selectedTopLevelIds : [],
    });

  return (
    <Stack gap="md" className={styles.panel}>
      <div>
        <Text size="xs" color="secondary" tt="uppercase">
          {periodLabel} — {t("settings:reception.fractionWindows.panelAcceptsLabel")}
        </Text>
        <Text size="lg" strong>
          {t("settings:reception.fractionWindows.fractionsCount", {
            selected: selectedTopLevelIds.length,
            total: allIds.length,
          })}
        </Text>
      </div>

      <SegmentedControls
        data={[
          { label: t("settings:reception.fractionWindows.modeAll"), value: "ALL" },
          { label: t("settings:reception.fractionWindows.modeNone"), value: "NONE" },
          { label: t("settings:reception.fractionWindows.modeMultiSelect"), value: "MULTI" },
        ]}
        value={mode}
        onChange={handleModeChange}
        fullWidth
      />

      <ScrollArea h={340}>
        <Stack gap={0}>
          {salesItems.map((item) => (
            <Checkbox
              key={item.salesItemId}
              label={item.salesItemTranslated}
              labelPosition="left"
              checked={selectedTopLevelIds.includes(item.salesItemId)}
              onChange={() => toggleFraction(item.salesItemId)}
              className={styles.listRow}
              styles={{ body: { justifyContent: "space-between" } }}
            />
          ))}
        </Stack>
      </ScrollArea>

      <Group justify="space-between" mt="auto">
        {onReset ? (
          <CTA intent="negate" size="sm" onClick={onReset}>
            {t("settings:reception.fractionWindows.resetWindow")}
          </CTA>
        ) : (
          <span />
        )}
        <Group gap="sm">
          <CTA intent="secondary" size="sm" onClick={onClose}>
            {t("settings:reception.fractionWindows.cancel")}
          </CTA>
          <CTA intent="primary" size="sm" onClick={handleApply} data-testid="apply-fraction-window">
            {t("settings:reception.fractionWindows.apply")}
          </CTA>
        </Group>
      </Group>
    </Stack>
  );
};

export default FractionWindowPanel;
